/* eslint-disable graphql-relay-compat/no-import-graphql-operations */
/* eslint-disable no-restricted-imports */
import { useEffect, useMemo, useState } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import { type InlineReactionsData } from '@post-office/growth-common/confluence-experiments-context';

import type {
	SpaceHoverPageCardQuery as SpaceHoverPageCardQueryData,
	SpaceHoverPageCardQueryVariables as SpaceHoverPageCardQueryVars,
} from '@confluence/page-card/src/__types__/SpaceHoverPageCardQuery.ts';
import { SpaceHoverPageCardQuery } from '@confluence/page-card/src/SpaceHoverPageCardQuery.graphql.ts';
import { ReactionsContentQuery } from '@confluence/reactions/src/graphql/ReactionsContentQuery.graphql.ts';
import type {
	ReactionsContentQuery as ReactionsContentQueryData,
	ReactionsContentQueryVariables as ReactionsContentQueryVars,
} from '@confluence/reactions/src/graphql/__types__/ReactionsContentQuery.ts';
import { ReactedUsersQuery } from '@confluence/reactions/src/graphql/ReactedUsersQuery.graphql.ts';
import type {
	ContainerType,
	GraphQLReactionContentType,
	ReactedUsersQuery as ReactedUsersQueryData,
	ReactedUsersQueryVariables,
} from '@confluence/reactions/src/graphql/__types__/ReactedUsersQuery.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { getPageDataFromUrl } from './getPageDataFromUrl.ts';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

type ReactedUserQueryResult = {
	data?: string[];
	loading: boolean;
	error?: Error;
};

const INITIAL_REACTED_USER_QUERY_RESULT: ReactedUserQueryResult = {
	data: undefined,
	loading: false,
	error: undefined,
};

// Helper function to fetch the specific names of users who reacted to a specific emoji
const getReactedUsersForEmoji = async (contentId: string, contentType: string, emojiId: string) => {
	const { data } = await queryInterop<ReactedUsersQueryData, ReactedUsersQueryVariables>({
		query: ReactedUsersQuery,
		variables: {
			contentId,
			contentType: contentType.toUpperCase() as GraphQLReactionContentType,
			containerId: contentId,
			containerType: contentType.toUpperCase() as ContainerType,
			emojiId,
		},
	});

	return data;
};

// Helper function to get unique users who reacted to any emoji
const getUniqueReactedUsers = async (
	contentId: string,
	contentType: string,
	emojiIds: string[],
) => {
	const uniqueUsers: Record<string, string> = {};

	// Process all emojis in parallel to get all unique users
	await Promise.allSettled(
		emojiIds.filter(Boolean).map(async (emojiId) => {
			const data = await getReactedUsersForEmoji(contentId, contentType, emojiId);

			// Add all users to the unique users map
			for (const user of data?.reactedUsers?.confluencePerson ?? []) {
				if (user?.displayName && user.accountId) {
					uniqueUsers[user.accountId] = user.displayName;
				}
			}
		}),
	);

	return uniqueUsers;
};

// Hook to fetch the page author, author's avatar URL, total reactions count, and the first
// {numberOfReactedUsers} users who reacted to the page.
export const useInlineReactionsData = (
	numberOfReactedUsers: number,
	skip: boolean = false,
): InlineReactionsData => {
	// Needed for Confluence-required analytic events
	const { userId, activationId } = useSessionData();

	const { contentId, contentType } = getPageDataFromUrl(window.location.href);
	const [reactedUsers, setReactedUsers] = useState<ReactedUserQueryResult>(
		INITIAL_REACTED_USER_QUERY_RESULT,
	);
	const [uniqueUsersCount, setUniqueUsersCount] = useState<number>(0);

	// Fetch page author and author's avatar URL
	const {
		data: spaceHoverPageCardResult,
		loading: spaceHoverPageCardLoading,
		error: spaceHoverPageCardError,
	} = useQuery<SpaceHoverPageCardQueryData, SpaceHoverPageCardQueryVars>(SpaceHoverPageCardQuery, {
		variables: {
			id: contentId!, // guaranteed to be defined because we skip the query if it's not
		},
		skip: !contentId || skip,
	});

	// Fetch total reactions count and emoji ID summary
	const {
		data: reactionsContentData,
		loading: reactionsLoading,
		error: reactionsError,
	} = useQueryInterop<ReactionsContentQueryData, ReactionsContentQueryVars>(ReactionsContentQuery, {
		variables: {
			contentId: contentId!, // guaranteed to be defined because we skip the query if it's not
		},
		skip: !contentId || skip,
	});

	// Use the emoji ID summary from reactionsContentData to fetch the specific names of users who reacted
	useEffect(() => {
		const emojiIds: string[] | undefined =
			reactionsContentData?.content?.nodes?.[0]?.contentReactionsSummary?.reactionsSummaryForEmoji?.flatMap(
				(reaction) => reaction?.emojiId ?? [],
			);

		// Need to put this in a function and then call it because we can't use async/await in useEffect
		const getReactedUsers = async () => {
			if (!contentId || !contentType || !emojiIds || reactedUsers.data || reactedUsers.loading) {
				return;
			}

			setReactedUsers({ loading: true });

			try {
				// Get all unique users in parallel
				const uniqueUsers = await getUniqueReactedUsers(contentId, contentType, emojiIds);

				// Take only the first numberOfReactedUsers users for the reactedUsers state
				const limitedUsers = Object.values(uniqueUsers).slice(0, numberOfReactedUsers);

				// Update state
				setUniqueUsersCount(Object.keys(uniqueUsers).length);
				setReactedUsers({
					data: limitedUsers,
					loading: false,
				});
			} catch (error) {
				setReactedUsers({
					loading: false,
					error: error as Error,
				});
			}
		};

		getReactedUsers();
	}, [contentId, contentType, reactionsContentData, reactedUsers, numberOfReactedUsers]);

	// Transform this so that it matches Post Office's expected shape (for use with the ReactionWrapper component)
	const transformedReactionsSummary = useMemo(
		() => ({
			reactionsSummary: {
				ari: reactionsContentData?.content?.nodes?.[0]?.contentReactionsSummary?.ari || '',
				containerAri:
					reactionsContentData?.content?.nodes?.[0]?.contentReactionsSummary?.containerAri || '',
				reactionsSummaryForEmoji:
					reactionsContentData?.content?.nodes?.[0]?.contentReactionsSummary?.reactionsSummaryForEmoji?.map(
						(emoji) => {
							// Only return emoji objects that have all required non-null values
							if (emoji?.count != null && emoji?.emojiId != null && emoji?.reacted != null) {
								return {
									count: emoji.count,
									emojiId: emoji.emojiId,
									reacted: emoji.reacted,
									__typename: 'ReactionsSummaryForEmoji' as const,
								};
							}
							return null; // Return null for incomplete emoji data
						},
					) || [], // Provide empty array as fallback if the whole map operation is undefined
				__typename: 'ReactionsSummaryResponse' as const,
			},
		}),
		[reactionsContentData],
	);

	return {
		data: {
			authorName:
				spaceHoverPageCardResult?.singleContent?.history?.ownedBy?.displayName ?? undefined,
			authorAvatar: spaceHoverPageCardResult?.singleContent?.history?.ownedBy?.profilePicture?.path,
			reactionsCount: uniqueUsersCount,
			reactionsSummary: transformedReactionsSummary,
			reactedUsers: reactedUsers.data,
			userId: userId || undefined,
			activationId,
		},
		loading: spaceHoverPageCardLoading || reactionsLoading || reactedUsers.loading,
		error: spaceHoverPageCardError || reactionsError || reactedUsers.error,
	};
};
